import type { AppContext } from "../../app/app";
import { purposesPresent } from "../../data/packs";
import { domainsPresent } from "../../data/domains";
import { assemble } from "../../llm/partner";
import { el } from "../dom";
import { renderPurposeFilter } from "../dimension-tools";
import { renderOnboardingHero, renderPurposeChooser, shouldChoosePurpose } from "../purpose";
import { requireProcessingConsent } from "../privacy";
import { renderReport } from "../report";
import { assemblePromptFor } from "./people";
import { modelError } from "./surface";

// ASSEMBLE: the front door. A reasoning brief for a decision, drawn only from
// stored judgements. Optionally opens with a prefilled prompt (e.g. resurfacing
// a known person), running it straight away.
export async function renderAssemble(
  ctx: AppContext,
  initialPrompt?: string,
): Promise<HTMLElement> {
  const root = el("section", {});

  const [people, tags, dimensions, topics, personTopics] = await Promise.all([
    ctx.repo.listPeople(),
    ctx.repo.listTags({ viewer_id: ctx.viewer }),
    ctx.repo.listDimensions(),
    ctx.repo.listTopics(),
    ctx.repo.listPersonTopics(),
  ]);

  // Empty state: a value-proposition hero, then lead the user to set a purpose
  // at the natural first moment. Choosing seeds dimensions and goes straight to
  // adding the first person.
  if (shouldChoosePurpose(dimensions.length, people.length)) {
    // Hero and the card grid share one centred container so their left and right
    // edges align on the same spine (no full-width grid under a narrow hero).
    root.append(
      el(
        "div",
        { class: "onboarding" },
        renderOnboardingHero(),
        el("h2", { class: "chooser-heading", text: "To begin, pick what you will use it for" }),
        renderPurposeChooser(ctx, { onSeeded: () => ctx.navigate({ name: "people" }) }),
      ),
    );
    return root;
  }

  root.append(
    el("span", { class: "label", text: "Assemble" }),
    el("h1", { text: "What decision are you weighing, or who are you assessing?" }),
  );

  // Cold start: a decision view is a dead end with nothing to reason over, so
  // route a first-run user to add/import and rate before assembling.
  const ratedCount = new Set(tags.map((t) => t.person_id)).size;
  if (ratedCount === 0) {
    root.append(onboarding(ctx, people.length));
    return root;
  }

  const output = el("div", {});

  // Optional purpose scope: narrow the brief to one purpose's dimensions.
  let scopeDims: string[] | undefined;
  const purposeFilter = renderPurposeFilter(purposesPresent(dimensions), (key) => {
    scopeDims = key === null ? undefined : dimensions.filter((d) => d.purposes.includes(key)).map((d) => d.id);
  });

  // Optional domain filter: narrow the brief to people flagged with a domain. A
  // separate axis from the purpose scope; naming a domain in the prompt also
  // works without selecting here.
  let scopeDomain: string[] | undefined;
  const domainFilter = renderPurposeFilter(domainsPresent(topics, personTopics), (key) => {
    scopeDomain = key === null ? undefined : [key];
  });

  const prompt = el("textarea", {
    class: "input textarea",
    placeholder:
      "e.g. Who would you put on the diligence call, and what would you watch " +
      "for? Or: what is my read on Avery Stone?",
  });
  prompt.value = initialPrompt ?? "";

  const run = el("button", {
    class: "btn btn--primary",
    text: "Assemble brief",
    on: {
      click: async () => {
        const decision = prompt.value.trim();
        if (!decision) return;
        if (!(await requireProcessingConsent(ctx))) return;
        run.disabled = true;
        output.replaceChildren(
          el("p", { class: "lede", text: "Drawing on your tags..." }),
        );
        try {
          const brief = await assemble(ctx.repo, ctx.provider, ctx.viewer, decision, scopeDims, scopeDomain);
          output.replaceChildren(renderReport(brief));
        } catch (e) {
          output.replaceChildren(modelError(ctx, e));
        } finally {
          run.disabled = false;
        }
      },
    },
  });

  root.append(
    el("p", {
      class: "lede",
      text:
        "Pose it in your own words. The brief names whom you rate that is " +
        "relevant, the tensions, and the gaps, using only your own stored " +
        "judgements. It never invents people.",
    }),
    prompt,
  );
  if (purposeFilter) {
    root.append(
      el("span", { class: "label", text: "Scope to a purpose (optional)" }),
      purposeFilter,
    );
  }
  if (domainFilter) {
    root.append(
      el("span", { class: "label", text: "Scope to a domain (optional)" }),
      domainFilter,
    );
  }
  root.append(
    el("div", { class: "panel-actions" }, run),
    output,
    await recentlyRelevant(ctx),
  );

  // Arrived with a prompt (resurfacing): run it immediately.
  if (initialPrompt && initialPrompt.trim()) void run.click();
  return root;
}

// First-run nudge: route to add/import and rate before the decision view can do
// anything useful.
function onboarding(ctx: AppContext, peopleCount: number): HTMLElement {
  const hasPeople = peopleCount > 0;
  return el(
    "div",
    { class: "notice" },
    el("p", {
      text: hasPeople
        ? `You have ${peopleCount} ${peopleCount === 1 ? "person" : "people"} but none rated yet. Rate someone on a quality or two, then come back to assemble a brief.`
        : "Nothing to reason over yet. Add the people whose judgement you have a read on, or import your contacts, then rate them on a few qualities. This view assembles a brief from those ratings.",
    }),
    el("button", {
      class: "btn btn--primary",
      text: hasPeople ? "Go and rate" : "Add or import people",
      on: { click: () => ctx.navigate({ name: "people" }) },
    }),
  );
}

// A lightweight, contextual "recently relevant" strip: your most recently rated
// people and your last few decisions, each a one-click way back into a brief.
// Derived from your own data only; no notifications, no engagement mechanics.
async function recentlyRelevant(ctx: AppContext): Promise<HTMLElement> {
  const [people, tags, decisions] = await Promise.all([
    ctx.repo.listPeople(),
    ctx.repo.listTags({ viewer_id: ctx.viewer }),
    ctx.repo.listDecisions(),
  ]);
  const wrap = el("div", { class: "recent" });

  const nameOf = new Map(people.map((p) => [p.id, p.display_name]));
  const latest = new Map<string, number>();
  for (const t of tags)
    latest.set(t.person_id, Math.max(latest.get(t.person_id) ?? 0, t.created_at));
  const recentPeople = [...latest.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([id]) => ({ id, name: nameOf.get(id) ?? "(unknown)" }));

  if (recentPeople.length > 0) {
    const chips = el("div", { class: "recent__chips" });
    for (const p of recentPeople) {
      chips.append(
        el("button", {
          class: "chip",
          text: p.name,
          on: {
            click: () =>
              ctx.navigate({ name: "assemble", prompt: assemblePromptFor(p.name) }),
          },
        }),
      );
    }
    wrap.append(el("span", { class: "label", text: "Recently rated" }), chips);
  }

  const recentDecisions = [...decisions].reverse().slice(0, 3);
  if (recentDecisions.length > 0) {
    const chips = el("div", { class: "recent__chips" });
    for (const d of recentDecisions) {
      const short = d.prompt.length > 60 ? `${d.prompt.slice(0, 60)}...` : d.prompt;
      chips.append(
        el("button", {
          class: "chip",
          text: short,
          title: d.prompt,
          on: { click: () => ctx.navigate({ name: "assemble", prompt: d.prompt }) },
        }),
      );
    }
    wrap.append(el("span", { class: "label", text: "Recent decisions" }), chips);
  }

  return wrap;
}
